/**
 * Reconcile an IV bar read against the CP and max HP on the same page
 * (2026-09-28 accuracy sweep).
 *
 * WHY. The three appraisal bars are read from pixels: a fill fraction per
 * bar, rounded to fifteenths. Every device paints the bars at its own scale,
 * a HUD shadow once dimmed a whole panel one step (2026-09-17), and a
 * divider pixel counts for neither side; so a bar is occasionally read one
 * step off. The page carries the arithmetic to catch that: CP and max HP
 * are exact functions of (base stats, IV triple, half-level), and for a
 * given CP + HP only a handful of triples exist at all. A read that fits is
 * right; a read one step off usually has exactly ONE neighbour that fits,
 * and that neighbour is the truth. Nothing here depends on the screen.
 *
 * RULES
 *   - exact:     the read triple sits on a curve for the species (any form)
 *                at CP + HP.
 *   - corrected: it does not, but exactly one triple within one step per
 *                stat (Chebyshev distance 1, up to 26 neighbours) does.
 *   - ambiguous: two or more such neighbours fit; the caller holds the frame.
 *   - none:      no neighbour fits either; the CP, HP or bars are wrong by
 *                more than a step and the frame is held.
 */
import { deriveLevel } from "./myPokemonDerived";
import { computeCp, computeHp } from "./ivSolver";

export interface IvTriple {
  atk: number;
  def: number;
  hp: number;
}

export interface IvReconcileResult {
  status: "exact" | "corrected" | "ambiguous" | "none";
  /** The triple to trust: the read (exact) or its unique fitting neighbour. */
  iv: IvTriple;
  /** The half-level the trusted triple sits at (exact / corrected only). */
  level: number | null;
  /** Every neighbour that fit, for the log. */
  candidates: IvTriple[];
}

type Base = { atk: number; def: number; sta: number };

function levelFor(rows: readonly Base[], iv: IvTriple, cp: number, hpMax: number | null): number | null {
  for (const base of rows) {
    const level = deriveLevel(base, { atk: iv.atk, def: iv.def, sta: iv.hp }, cp, hpMax);
    if (level != null) return level;
  }
  return null;
}

export function reconcileIvRead(
  rows: readonly Base[],
  read: IvTriple,
  cp: number,
  hpMax: number | null,
): IvReconcileResult {
  const exact = levelFor(rows, read, cp, hpMax);
  if (exact != null) return { status: "exact", iv: read, level: exact, candidates: [read] };
  const candidates: Array<{ iv: IvTriple; level: number }> = [];
  for (let da = -1; da <= 1; da++) {
    for (let dd = -1; dd <= 1; dd++) {
      for (let dh = -1; dh <= 1; dh++) {
        if (da === 0 && dd === 0 && dh === 0) continue;
        const iv = { atk: read.atk + da, def: read.def + dd, hp: read.hp + dh };
        if (iv.atk < 0 || iv.def < 0 || iv.hp < 0 || iv.atk > 15 || iv.def > 15 || iv.hp > 15) continue;
        const level = levelFor(rows, iv, cp, hpMax);
        if (level != null) candidates.push({ iv, level });
      }
    }
  }
  if (candidates.length === 1) {
    return { status: "corrected", iv: candidates[0].iv, level: candidates[0].level, candidates: [candidates[0].iv] };
  }
  return {
    status: candidates.length === 0 ? "none" : "ambiguous",
    iv: read,
    level: null,
    candidates: candidates.map((c) => c.iv),
  };
}

/**
 * How far a CP read may sit from the CP the bars and max HP predict before
 * it is the CP that is wrong, not the bars. A bar one step off moves the CP
 * by about a percent; a digit the model's horns covered moves it by a
 * multiple (2026-10-06 Galaxy log: Xerneas CP 3478 read as 78, Gyarados and
 * Kyogre likewise, every frame, so the hold never cleared and the HUD showed
 * ?/?/? over bars it had read cleanly).
 */
const CP_MISREAD_RATIO = 0.15;

/**
 * True when the read triple and the max HP agree with each other at some
 * half-level, and the CP read is nowhere near the CP they predict there. The
 * caller then drops the CP and keeps the bars (which still need their
 * three-frame agreement) instead of holding the whole frame. A CP within the
 * ratio stays a hold: that close, a wrong bar is as likely as a wrong digit.
 */
export function cpLooksMisread(
  rows: readonly Base[],
  read: IvTriple,
  cp: number,
  hpMax: number,
): boolean {
  let anyLevel = false;
  for (const base of rows) {
    const iv = { atk: read.atk, def: read.def, sta: read.hp };
    for (let level = 1; level <= 51; level += 0.5) {
      if (computeHp(base, read.hp, level) !== hpMax) continue;
      anyLevel = true;
      const predicted = computeCp(base, iv, level);
      if (Math.abs(predicted - cp) <= CP_MISREAD_RATIO * predicted) return false;
    }
  }
  return anyLevel;
}
